// URL search-param contract for the /exports route (ui-spec §10.12 + §7.2).
// Period is the shared Period (ADR-0021 §1): ?period=2026-03 | 2026-Q2 |
// 2026 or ?from=&to=, read by the same parser as every other page, so old
// ?period=YYYY-MM links keep opening that month; no period means the
// current month. `accounts` is a list of account slugs the user has scoped
// to; `format` is one of the v1 formats. JSON is still deferred (SPEC §11);
// DATEV ZIP landed with issue #18 and is exposed as the third option.
// `belege` (plain receipt-file ZIP) and `moneymoney` (per-account
// transaction CSV) are the Steuerberater-workflow formats (2026-07);
// `onlyNew` is the belege delta toggle, default ON and serialized only
// when switched off.

import {
  type InvalidPeriod,
  type Period,
  parseLinkPeriod,
  serializeLinkPeriod,
} from './schemas.ts';

export const EXPORTS_FORMATS = ['agenda', 'csv', 'datev', 'extf', 'belege', 'moneymoney'] as const;
export type ExportFormat = (typeof EXPORTS_FORMATS)[number];
const DEFAULT_FORMAT: ExportFormat = 'agenda';

export interface ExportsSearchState {
  readonly period: Period;
  /**
   * Set when the link's period could not be read (see LinkPeriod). The
   * composer shows the reason and holds the download back instead of
   * silently exporting a different period.
   */
  readonly invalidPeriod: InvalidPeriod | null;
  readonly accounts: readonly string[];
  readonly format: ExportFormat;
  readonly onlyNew: boolean;
}

function parseAccounts(value: unknown): readonly string[] {
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function parseFormat(value: unknown): ExportFormat {
  if (typeof value !== 'string') return DEFAULT_FORMAT;
  return (EXPORTS_FORMATS as readonly string[]).includes(value)
    ? (value as ExportFormat)
    : DEFAULT_FORMAT;
}

function parseOnlyNew(value: unknown): boolean {
  // Default ON; only the explicit opt-out round-trips through the URL.
  return value !== 'false' && value !== false;
}

export function parseExportsSearch(input: Record<string, unknown>): ExportsSearchState {
  return {
    ...parseLinkPeriod(input),
    accounts: parseAccounts(input.accounts),
    format: parseFormat(input.format),
    onlyNew: parseOnlyNew(input.onlyNew),
  };
}

export function serializeExportsSearch(state: ExportsSearchState): Record<string, unknown> {
  const out: Record<string, unknown> = { ...serializeLinkPeriod(state) };
  if (state.accounts.length > 0) out.accounts = state.accounts.join(',');
  if (state.format !== DEFAULT_FORMAT) out.format = state.format;
  // A boolean, so TanStack Router writes a clean `?onlyNew=false`; the
  // string would be JSON-quoted (`?onlyNew=%22false%22`). parseOnlyNew
  // reads both.
  if (!state.onlyNew) out.onlyNew = false;
  return out;
}
